/**
 * The reseller permission catalog — TypeScript copy of crm-api's
 * `ResellerPermissions` (spec F §2–§3).
 *
 * crm-api is the source of truth and ENFORCES from its own constant on every
 * request. This copy exists so the UI can be typed and rendered without a
 * network round trip; `cypress/e2e/permission_catalog.cy.ts` fetches crm-api's
 * catalog and deep-equals it to these values, so the two cannot drift silently.
 *
 * Nothing here is a gate. `effectiveResellerPermissions` mirrors the server's
 * formula so the Permissions drawer can preview a change live; the server
 * recomputes it from the live membership when the change is saved.
 */

export const RESELLER_ROLES = ["owner", "salesperson", "technician", "viewer"] as const;
export type ResellerRole = (typeof RESELLER_ROLES)[number];

export const EDITABLE_RESELLER_ROLES = ["salesperson", "technician", "viewer"] as const;
export type EditableResellerRole = (typeof EDITABLE_RESELLER_ROLES)[number];

export const RESELLER_ROLE_LABELS: Record<ResellerRole, string> = {
  owner: "Owner",
  salesperson: "Salesperson",
  technician: "Technician",
  viewer: "Viewer",
};

/**
 * CUSTOM ROLES (2026-09-15). On top of the four built-ins, a reseller company
 * may carry roles staff invented for it ("Field engineer"). crm-api mints the
 * key from the name ONCE — `custom_field_engineer` — and keeps it stable across
 * renames, so a membership's `role` never has to be rewritten when the label
 * changes. A custom role has NO shipped default bundle: it starts empty and
 * holds exactly what was ticked for it in that company's matrix.
 *
 * The key shape is crm-api's validation, copied: anything else is refused
 * upstream, so the BFF refuses it first.
 */
export type CustomResellerRoleKey = `custom_${string}`;
/** Any role a membership can carry: a built-in, or a custom key of its company. */
export type ResellerRoleKey = ResellerRole | CustomResellerRoleKey;
/** A row of the company's matrix: the three editable built-ins plus its custom roles. */
export type MatrixRoleKey = EditableResellerRole | CustomResellerRoleKey;

export const CUSTOM_ROLE_KEY = /^custom_[a-z0-9_]{1,50}$/;
/** crm-api's name cap; the BFF validates to the same length. */
export const CUSTOM_ROLE_NAME_MAX = 60;

export function isCustomRoleKey(value: unknown): value is CustomResellerRoleKey {
  return typeof value === "string" && CUSTOM_ROLE_KEY.test(value);
}

export function isEditableResellerRole(value: unknown): value is EditableResellerRole {
  return typeof value === "string" && (EDITABLE_RESELLER_ROLES as readonly string[]).includes(value);
}

export function isResellerRoleKey(value: unknown): value is ResellerRoleKey {
  return (typeof value === "string" && (RESELLER_ROLES as readonly string[]).includes(value)) || isCustomRoleKey(value);
}

/** One row of crm-api's `GET /api/v1/resellers/:id/role_permissions`. `custom`
 * and `label` arrived with custom roles; an older crm-api omits both, and then
 * every row is a built-in whose label comes from `RESELLER_ROLE_LABELS`. */
export interface ResellerRoleMatrixEntry {
  permissions: string[];
  is_default: boolean;
  custom?: boolean;
  label?: string;
}

/** The company's role matrix, forwarded verbatim. Owner is never a row. */
export interface ResellerRoleMatrix {
  roles: Record<string, ResellerRoleMatrixEntry>;
  /** Display order crm-api chose (built-ins, then custom by creation). Optional. */
  order?: string[];
}

/** A matrix row as every surface renders it — already defaulted and ordered. */
export interface ResellerRoleRow {
  key: MatrixRoleKey;
  label: string;
  custom: boolean;
  permissions: string[];
  isDefault: boolean;
}

/** "custom_field_engineer" → "Field engineer": only used when crm-api sent a
 * custom key with no label, so the operator still reads a word, not a slug. */
function humanizeCustomKey(key: string): string {
  const words = key.replace(/^custom_/, "").replace(/_+/g, " ").trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : key;
}

/**
 * The label for any role key. Built-ins read the fixed constant (they cannot
 * be renamed); a custom key reads the matrix, then the membership's own
 * `role_label`, then a humanised key — so a roster row still names its role
 * while the matrix is loading or failed.
 */
export function roleLabel(
  role: string,
  matrix?: Pick<ResellerRoleMatrix, "roles"> | null,
  hint?: string | null,
): string {
  if ((RESELLER_ROLES as readonly string[]).includes(role)) return RESELLER_ROLE_LABELS[role as ResellerRole];
  const fromMatrix = matrix?.roles?.[role]?.label;
  if (fromMatrix) return fromMatrix;
  if (hint) return hint;
  return humanizeCustomKey(role);
}

/**
 * The ordered rows of a company's matrix: the three editable built-ins first
 * (always present — a row crm-api omits means the shipped default applies),
 * then the custom roles in crm-api's `order`, then any custom key `order` did
 * not name. Keys that are neither (e.g. a stray `owner`) are dropped.
 */
export function matrixRows(matrix: ResellerRoleMatrix | null | undefined): ResellerRoleRow[] {
  const roles = matrix?.roles ?? {};
  const builtIns: ResellerRoleRow[] = EDITABLE_RESELLER_ROLES.map((key) => {
    const entry = roles[key];
    return {
      key,
      label: RESELLER_ROLE_LABELS[key],
      custom: false,
      permissions: entry?.permissions ?? [...DEFAULT_ROLE_PERMISSIONS[key]],
      isDefault: entry ? entry.is_default : true,
    };
  });
  const customKeys: CustomResellerRoleKey[] = [];
  for (const key of [...(matrix?.order ?? []), ...Object.keys(roles)]) {
    if (isCustomRoleKey(key) && roles[key] && !customKeys.includes(key)) customKeys.push(key);
  }
  const custom: ResellerRoleRow[] = customKeys.map((key) => ({
    key,
    label: roleLabel(key, matrix),
    custom: true,
    permissions: roles[key]?.permissions ?? [],
    isDefault: false,
  }));
  return [...builtIns, ...custom];
}

/** Every role a member can be assigned at this company, labelled, in picker
 * order: Owner, the built-ins, then the company's custom roles. */
export function assignableRoles(
  matrix: ResellerRoleMatrix | null | undefined,
): { key: ResellerRoleKey; label: string }[] {
  return [
    { key: "owner", label: RESELLER_ROLE_LABELS.owner },
    ...matrixRows(matrix).map((r) => ({ key: r.key, label: r.label })),
  ];
}

export const PERMISSION_SECTIONS = {
  customers: "Customers",
  contacts: "Contacts",
  prospects: "Prospects",
  invoices: "Invoices & quotes",
  licences: "Licences",
  documentation: "Documentation",
  // `settings` was a section until 2026-09-14: the reseller portal's Settings
  // page (branding, payment identity) is GONE — a reseller issues no documents
  // of its own any more, so there is nothing to brand — and its two keys went
  // with it. crm-api dropped it the same day; this copy must not lag it.
  /** LEGAL — the company's OWN paperwork (2026-09-13; labelled "Legal" since
   * 2026-09-17): the Reseller Agreement + NDA it signed and XOP countersigned,
   * and the company file cabinet. READING it needs no grant: the portal's Legal
   * page is visible to every active member of the company, even when nothing
   * has been filed yet (CEO, 2026-09-17), so there is no `documents:view` key.
   * `documents:edit` files paperwork into the cabinet; no editable role holds
   * it by default — the owner grants it. */
  documents: "Legal",
} as const;
export type PermissionSection = keyof typeof PERMISSION_SECTIONS;

export const PERMISSION_VERBS = ["view", "edit"] as const;
export type PermissionVerb = (typeof PERMISSION_VERBS)[number];

/** Every section × verb key except the ones that do not exist (`documents:view`). */
export type ResellerPermission = Exclude<`${PermissionSection}:${PermissionVerb}`, "documents:view">;

const SECTION_KEYS = Object.keys(PERMISSION_SECTIONS) as PermissionSection[];

/** Section × verb keys that do NOT exist. `documents:view` left on 2026-09-17:
 * the Legal page is readable by any active member, so a read grant for it
 * would gate nothing. Mirrors crm-api's `ALL = … - %w[documents:view]`. */
const ABSENT_PERMISSIONS: readonly string[] = ["documents:view"];

/** Catalog order — every list this module returns is sorted by it. */
export const ALL_PERMISSIONS: readonly ResellerPermission[] = SECTION_KEYS.flatMap((s) =>
  PERMISSION_VERBS.map((v) => `${s}:${v}`),
).filter((k): k is ResellerPermission => !ABSENT_PERMISSIONS.includes(k));

export const UNGRANTABLE_PERMISSIONS: readonly ResellerPermission[] = ["documentation:edit"];

export const GRANTABLE_PERMISSIONS: readonly ResellerPermission[] = ALL_PERMISSIONS.filter(
  (k) => !UNGRANTABLE_PERMISSIONS.includes(k),
);

const ALL_SET = new Set<string>(ALL_PERMISSIONS);

export function isResellerPermission(value: unknown): value is ResellerPermission {
  return typeof value === "string" && ALL_SET.has(value);
}

/** Cross-section implications (spec F §2.1); `edit ⇒ view` is generic. */
const CROSS_IMPLICATIONS: Partial<Record<ResellerPermission, ResellerPermission[]>> = {
  "licences:view": ["customers:view"],
  "invoices:edit": ["customers:view"],
};

/** Every implication, including the generic ones — the same map crm-api's
 * `implications_json` serves, so "implied by …" can be shown without
 * re-deriving the rule. */
export const PERMISSION_IMPLICATIONS: Readonly<Record<string, ResellerPermission[]>> = Object.fromEntries(
  ALL_PERMISSIONS.flatMap((key) => {
    const [section, verb] = key.split(":") as [PermissionSection, PermissionVerb];
    const implied: ResellerPermission[] = [];
    const view: string = `${section}:view`;
    if (verb === "edit" && isResellerPermission(view)) implied.push(view);
    implied.push(...(CROSS_IMPLICATIONS[key] ?? []));
    // `documents:edit` implies no read key — reading the Legal page is ungated.
    return implied.length > 0 ? [[key, implied]] : [];
  }),
);

export const DEFAULT_ROLE_PERMISSIONS: Record<EditableResellerRole, ResellerPermission[]> = {
  salesperson: [
    "customers:view", "customers:edit", "contacts:view", "contacts:edit",
    "prospects:view", "prospects:edit",
    // Salespeople create licences (CEO, 2026-09-17); crm-api keeps trials to a
    // customer's first licence and holds a full one for super admin approval.
    "invoices:view", "invoices:edit", "licences:view", "licences:edit", "documentation:view",
  ],
  technician: ["customers:view", "contacts:view", "licences:view", "licences:edit", "documentation:view"],
  viewer: [
    "customers:view", "contacts:view", "prospects:view", "invoices:view", "licences:view",
    "documentation:view",
  ],
};

/** Close a set under the implication rules; unknown keys are dropped. */
export function closurePermissions(keys: readonly string[]): ResellerPermission[] {
  const set = new Set<ResellerPermission>(keys.filter(isResellerPermission));
  let size = -1;
  while (size !== set.size) {
    size = set.size;
    for (const key of [...set]) for (const implied of PERMISSION_IMPLICATIONS[key] ?? []) set.add(implied);
  }
  return ALL_PERMISSIONS.filter((k) => set.has(k));
}

/** THE formula, mirrored from crm-api (spec F §4.2). */
export function effectiveResellerPermissions(input: {
  role: ResellerRoleKey | string;
  bundle?: readonly string[] | null;
  grants?: readonly string[];
  denies?: readonly string[];
}): ResellerPermission[] {
  if (input.role === "owner") return [...ALL_PERMISSIONS];
  // A custom role has no shipped default — without its company's row it
  // grants nothing, which is the safe reading of "we don't know".
  const base = input.bundle ?? (isEditableResellerRole(input.role) ? DEFAULT_ROLE_PERMISSIONS[input.role] : []);
  const denies = new Set(input.denies ?? []);
  const union = [...base, ...(input.grants ?? [])].filter((k) => !denies.has(k));
  return closurePermissions(union);
}

/** Which of `held` force `key` on through the closure — for "implied by …". */
export function impliedBy(key: ResellerPermission, held: readonly string[]): ResellerPermission[] {
  return held
    .filter(isResellerPermission)
    .filter((h) => h !== key && closurePermissions([h]).includes(key));
}

/** One line for a roster row: role, then any exceptions. */
export function summarizeOverrides(
  m: {
    role: ResellerRoleKey | string;
    /** crm-api's label for the membership's role (custom roles). */
    role_label?: string | null;
    permission_grants?: readonly string[] | null;
    permission_denies?: readonly string[] | null;
  },
  matrix?: Pick<ResellerRoleMatrix, "roles"> | null,
): string {
  if (m.role === "owner") return `${RESELLER_ROLE_LABELS.owner} · everything`;
  const parts = [roleLabel(m.role, matrix, m.role_label)];
  for (const g of m.permission_grants ?? []) parts.push(`+${g}`);
  for (const d of m.permission_denies ?? []) parts.push(`−${d}`);
  return parts.join(" · ");
}
